// Import required libraries
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import AnchorIcon from '@mui/icons-material/Anchor';
// import chatMultiple from '../../assets/images/Chat Multiple.svg';
import { IconButton } from "@mui/material";

import { useNavigate } from "react-router-dom";

import "./SideNav.scss";

// Import data & utils
import { SideNavDataTop, SideNavDataBottom } from "./SideNavData";
import { useTheme } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../Modal/Modal";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import WidgetsIcon from "@mui/icons-material/Widgets";
import {
  updateDatasetRegisterData,
  updateReportRegisterData,
  updateStandAloneAppRegisterData,
} from "../../redux/slice/selectionsSlice";
import useAnalyticsEventTracker from "../../hooks/useAnalyticsEventTracker";
import { getPersonaFilteredList } from "../../utils/utils";
import APP_CONSTANTS from "../../utils/config";
import { LightTooltip } from "../../kit/LightToolTip";

function Sidenav() {
  const theme = useTheme();
  const location = useLocation();
  const role = useSelector((state) => state.auth.metadata.role);
  const isDarkMode = useSelector((state) => state.selections.isDarkMode);
  const [showPopup, setShowPopup] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [redirectLink, setRedirectLink] = useState("");
  const selections = useSelector((state) => state.selections);
  const sidenavRef = useRef(null);

  // Collapse when clicking outside while expanded
  useEffect(() => {
    if (isCollapsed) return;
    const handleClickOutside = (event) => {
      if (sidenavRef.current && !sidenavRef.current.contains(event.target)) {
        setIsCollapsed(true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCollapsed]);
  const feature_disabled = useSelector(
    (state) => state.auth.metadata.feature_disabled?.sidenav || []
  );

  // Fetch the accessList and userRole from the store
  const accessList = useSelector(
    (state) => state.auth.metadata?.accessList || []
  );
  const userRole = useSelector((state) => state.auth.metadata?.role || "user");
  const is_brain_access = useSelector(
    (state) => state.auth.metadata?.brain?.access || false
  );

  const has_harbor_access = useSelector(
    (state) => state.auth.metadata?.harbor?.access || false
  );

  const gaEventTracker = useAnalyticsEventTracker("SideNav");

  const handleLinkClick = (el) => (event) => {
    gaEventTracker(`sidenav_btn_clicked`, `redirect_to = ${el}`);
    if (
      !location.pathname.includes("/rapid") &&
      (Object.keys(selections?.datasetRegisterData || {}).length > 0 ||
        Object.keys(selections?.reportRegisterData || {}).length > 0 ||
        Object.keys(selections?.standAloneAppRegisterData || {}).length > 0) &&
      location.pathname.includes("/register")
    ) {
      event.preventDefault(); // Prevent the default link behavior (navigation)
      setRedirectLink(el);
      setShowPopup(true);
    }
  };

  const handlePopupConfirm = () => {
    setShowPopup(false);
    dispatch(updateReportRegisterData({}));
    dispatch(updateDatasetRegisterData({}));
    dispatch(updateStandAloneAppRegisterData({}));
    navigate(`/${redirectLink}`);
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getID = (path) => {
    let mapObj = {
      home: "sidenav_home",
      discover: "sidenav_0",
      assist: "sidenav_1",
      myapps: "sidenav_2",
      saved: "sidenav_3",
      rapid: "sidenav_4",
      admin: "sidenav_5",
      resources: "sidenav_6",
      faq: "sidenav_7",
      help: "sidenav_8",
    };
    return mapObj[path];
  };

  const renderIcon = (iconName, path) => {
    const isSelected = location.pathname.startsWith(`/${path}`);
    const baseIconStyle = {
      minWidth: "20px",
      width: "22px",
      height: "22px",
      fontSize: "20px",
      marginRight: isCollapsed ? "0px" : "8px",
      display: "inline-block",
      verticalAlign: "middle",
    };
    const maskIcon = (src) => (
      <span
        style={{
          ...baseIconStyle,
          WebkitMaskImage: `url(${src})`,
          maskImage: `url(${src})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
          transform: "scale(1.05)",
          backgroundColor: isSelected
            ? "#FFFFFF"
            : isDarkMode === "dark"
            ? theme.palette.primary.darkViolet
            : "#1D387E",
        }}
      />
    );
    if (path === "home") {
      return (
        <HomeIcon
          sx={{
            fontSize: 20,
            color: isSelected
              ? "#FFFFFF"
              : isDarkMode === "dark"
              ? theme.palette.primary.darkViolet
              : "#1D387E",
            minWidth: "20px",
            marginRight: isCollapsed ? "0px" : "8px",
          }}
        />
      );
    }
    if (path === "myapps") {
      return (
        <WidgetsOutlinedIcon
          sx={{
            fontSize: 20,
            color: isSelected
              ? "#FFFFFF"
              : isDarkMode === "dark"
              ? theme.palette.primary.darkViolet
              : "#1D387E",
            minWidth: "20px",
            marginRight: isCollapsed ? "0px" : "8px",
          }}
        />
      );
    }
    if (path === "saved") {
      return (
        <BookmarkBorderOutlinedIcon
          sx={{
            fontSize: 20,
            color: isSelected
              ? "#FFFFFF"
              : isDarkMode === "dark"
              ? theme.palette.primary.darkViolet
              : "#1D387E",
            minWidth: "20px",
            marginRight: isCollapsed ? "0px" : "8px",
          }}
        />
      );
    }
    if (path === "help") {
      return (
        <HelpOutlineOutlinedIcon
          sx={{
            fontSize: 20,
            color: isSelected
              ? "#FFFFFF"
              : isDarkMode === "dark"
              ? theme.palette.primary.darkViolet
              : "#1D387E",
            minWidth: "20px",
            marginRight: isCollapsed ? "0px" : "8px",
          }}
        />
      );
    }
    if (path === "assist") {
      return maskIcon("/chat_multiple.svg");
    }
    if (path === "rapid") {
      return maskIcon("/content_view_gallery_lightning.svg");
    }
    if (path === "admin") {
      return maskIcon("/people_settings.svg");
    }
    if (path === "resources") {
      return maskIcon("/document_queue_multiple.svg");
    }
    if (path === "faq") {
      return maskIcon("/chat_bubbles_question.svg");
    }

    if (path === "harbor") {
      return (
        <AnchorIcon
          sx={{
            minWidth: "20px",
            width: "22px",
            height: "22px",
            fontSize: "20px",
            marginRight: isCollapsed ? "0px" : "8px",
            color: isSelected
              ? "#FFFFFF"
              : isDarkMode === "dark"
              ? theme.palette.primary.darkViolet
              : "#1D387E",
          }}
        />
      );
    }

    return (
      <i className="material-icons-outlined nav-icon">
        {iconName ? iconName : "person"}
      </i>
    );
  };

  const SideNavDataBottomData = SideNavDataBottom;
  // role === "user" || role === "owner"
  //   ? [
  //       {
  //         title: "Docs",
  //         path: "resources",
  //         icon: "smart_display",
  //         group: "bottom",
  //       },
  //       { title: "FAQs", path: "faq", icon: "quiz", group: "bottom" },
  //       { title: "Help", path: "help", icon: "help", group: "bottom" },
  //     ]
  //   : SideNavDataBottom;

  // Create a modified list that always includes Home at the top
  const homeItem = { title: "Home", path: "home", icon: "home", group: "top" };
  const brain = {
    title: "Brain 2.0",
    path: "brain",
    icon: "psychology",
    group: "top",
  };

  const harbor = {
    title: "Harbor",
    path: "harbor",
    icon: "anchor",
    group: "top",
  };

  const otherItems = SideNavDataTop.filter((item) => item.path !== "home");

  // Apply filtering to non-home items only
  const filteredOtherItems = getPersonaFilteredList(
    otherItems,
    accessList,
    APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV,
    userRole,
    "title"
  );

  // Always include Home at the top
  const filtered_SideNavDataTop = is_brain_access
    ? [homeItem, ...filteredOtherItems, brain]
    : [homeItem, ...filteredOtherItems];

  // Check access for harbor
  if (has_harbor_access) {
    filtered_SideNavDataTop.push(harbor);
  };

  const filtered_SideNavDataBottom = getPersonaFilteredList(
    SideNavDataBottomData,
    accessList,
    APP_CONSTANTS.PERSONA_PAGE.SIDE_NAV,
    userRole,
    "title"
  );

  return (
    <>
      <nav
        ref={sidenavRef}
        className={`nav-collapsed ${isDarkMode === "dark" ? "dark" : "light"} ${
          isCollapsed ? "collapsed" : "expanded"
        }`}
        style={{
          backgroundColor:
            isDarkMode === "dark" ? theme.palette.navbar.main : "#E5F3F7",
        }}
      >
        {/* Toggle Button */}
        {/* <div className={`toggle-button-container ${isCollapsed ? 'collapsed' : 'expanded'}`}>
          <IconButton
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            size="small"
            sx={{
              color: isDarkMode === 'dark' ? 'white' : '#556a9f',
              padding: '8px',
              '&:hover': {
                backgroundColor: isDarkMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(85, 106, 159, 0.1)',
              },
            }}
          >
            {isCollapsed ? (
              <MenuIcon 
                sx={{ 
                  transition: 'all 0.3s ease'
                }} 
              />
            ) : (
              <FirstPageIcon 
                sx={{ 
              
                  transition: 'all 0.3s ease'
                }} 
              />
            )}
          </IconButton>
        </div> */}
        {/* Toggle Button */}
        <div
          className={`toggle-button-container ${
            isCollapsed ? "collapsed" : "expanded"
          }`}
          style={{
            display: "flex",
            justifyContent: isCollapsed ? "center" : "flex-end", // ⬅️ change here
            width: "100%",
          }}
        >
          <IconButton
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            size="small"
            sx={{
              color: isDarkMode === "dark" ? "white" : "#556a9f",
              padding: "8px",
              "&:hover": {
                backgroundColor:
                  isDarkMode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(85, 106, 159, 0.1)",
              },
            }}
          >
            {isCollapsed ? (
              <MenuIcon sx={{ transition: "all 0.3s ease" }} />
            ) : (
              <FirstPageIcon sx={{ transition: "all 0.3s ease" }} />
            )}
          </IconButton>
        </div>

        <div className="nav-items-top">
          {filtered_SideNavDataTop
            .filter((element) => !feature_disabled.includes(element.path))
            .map((el, index) => (
              <Link
                id={getID(el.path)}
                key={el.path}
                to={`${el.path}`}
                className={
                  location.pathname.startsWith(`/${el.path}`)
                    ? `nav-row-selected light`
                    : `nav-row light`
                }
                style={{
                  borderLeft: location.pathname.startsWith(`/${el.path}`)
                    ? `3px solid ${theme.palette.navbar.active}`
                    : `3px solid transparent`,
                }}
                onClick={handleLinkClick(el.path)}
                // title={isCollapsed ? el.title : ""}
              >
                {isCollapsed ? (
                  <LightTooltip
                    title={el.title}
                    placement="right"
                    arrow
                    sx={{
                      zIndex: 9999,
                      "& .MuiTooltip-tooltip": {
                        padding: "8px 12px",
                      },
                    }}
                  >
                    {renderIcon(el.icon, el.path)}
                  </LightTooltip>
                ) : (
                  renderIcon(el.icon, el.path)
                )}

                {!isCollapsed && <p className="row-name">{el.title}</p>}
              </Link>
            ))}
        </div>

        <div className="nav-items-bottom">
          {filtered_SideNavDataBottom
            .filter((element) => !feature_disabled.includes(element.path))
            .map((el, index) => (
              <Link
                id={getID(el.path)}
                key={el.path}
                to={`${el.path}`}
                className={
                  location.pathname.startsWith(`/${el.path}`)
                    ? `nav-row-selected light`
                    : `nav-row light`
                }
                style={{
                  borderLeft: location.pathname.startsWith(`/${el.path}`)
                    ? `3px solid ${theme.palette.navbar.active}`
                    : `3px solid transparent`,
                }}
                onClick={handleLinkClick(el.path)}
                // title={isCollapsed ? el.title : ""}
              >
                {isCollapsed ? (
                  <LightTooltip
                    title={el.title}
                    placement="right"
                    arrow
                    sx={{
                      zIndex: 9999,
                      "& .MuiTooltip-tooltip": {
                        padding: "8px 12px",
                      },
                    }}
                  >
                    {renderIcon(el.icon, el.path)}
                  </LightTooltip>
                ) : (
                  renderIcon(el.icon, el.path)
                )}
                {!isCollapsed && <p className="row-name">{el.title}</p>}
              </Link>
            ))}
        </div>
      </nav>
      {showPopup && (
        <Modal
          headerMessage="Are you sure you want to discard all the changes?"
          cancelButtonText="No"
          submitButtonText="Yes"
          onCancel={() => setShowPopup(false)}
          handleSubmit={handlePopupConfirm}
        />
      )}
    </>
  );
}

export default Sidenav;
